/*
 * Copyright (C) 2019 Frontier Developments PLC.
 */

/**
 * VueX Global Types file
 */
export interface RootState {
    version: string;
    build?: string;
}

/**
 * JsonLD item representation
 */
export interface JsonLdItem {
    '@context': string;
    '@id':	string;
    '@type': string;
}

/**
 * JsonLD collection.
 * Uses generics to model collections json-ld items.
 */
export interface JsonLdCollection<T extends JsonLdItem> {
    '@context': string;
    '@id':	string;
    '@type': string;
    'hydra:member': T[];
    'hydra:totalItems': number;
    'hydra:search': HydraSearch;
    'hydra:view'?: HydraView;
}

/**
 * Hydra view interface.
 * Represents the page configuration for the result set.
 */
export interface HydraView {
    '@id':	string;
    '@type': string;
    'hydra:first': string;
    'hydra:last':	string;
    'hydra:previous': string;
    'hydra:next': string;
}

/**
 * Hydra search interface.
 * Represents the search configuration for the result set.
 */
export interface HydraSearch {
    '@type': string;
    'hydra:template': string;
    'hydra:variableRepresentation':	string;
    'hydra:mapping': HydraMapping;
}

/**
 * Hydra mapping.
 * Represents the mapping for each configurable search field in a result set.
 */
export interface HydraMapping {
    '@type': string;
    variable: string;
    property: string;
    required: boolean;
}

/**
 * Sort direction enumerations
 */
export enum SortDirection {
    ASC = 'asc',
    DESC = 'desc',
    NONE = '',
}

/**
 * Sort interface.
 * Contains information on the sort state of state data.  We leave it open as to the name and type of this data
 * for ease of addition of new sort types.
 */
export interface SortByInterface {
    [key: string]: undefined|SortDirection;
}

/**
 * Interface used to update sort by filters
 */
export interface SortByUpdateInterface {
    name: string;
    direction: SortDirection;
}

/**
 * Filter interface.
 * Contains information on the filter state of state data.  We leave it open as to the name and type of this data
 * for ease of addition of new filter types.
 */
export interface FilterByInterface {
    [key: string]: boolean|string|number|undefined|(boolean|string|number)[];
}

/**
 * Interface used to update filters
 */
export interface FilterUpdateInterface {
    name: string;
    value?: boolean|string|number|any|(boolean|string|number)[];
}

/**
 * State interface decorator that adds potential for paginated results.
 */
export interface PaginatableStateInterface {
    pagination: PaginationInformationInterface;
}

/**
 * Pagination data interface.
 */
export interface PaginationInformationInterface {
    totalItems: number;
    current: string;
    first: string;
    next: string;
    previous: string;
    last: string;
}

/**
 * State interface decorator that adds potential for loaded results.
 */
export interface LoadableStateInterface {
    loadStatus: LoadStatusInterface;
}

/**
 * Representation of a data load status.
 */
export interface LoadStatusInterface {
    loading: boolean;
    error: boolean;
    success: boolean;
}

/**
 * Implementation of LoadStatusInterface.
 */
export class LoadStatus implements LoadStatusInterface {
    public loading: boolean = false;
    public error: boolean = false;
    public success: boolean = false;
}

/**
 * Animal sex enumerations
 */
export enum AnimalSex {
    MALE = 'm',
    FEMALE = 'f',
    UNISEX = 'u',
}
